'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useLabsSurface } from './labs-surface';
import { useLabsStore } from './pack-context';
import { useLabsAnalytics } from './use-labs-analytics';
import { useNeedsPayment } from './use-needs-payment';

/**
 * How long to wait for the analytics flags before showing the trial CTA anyway.
 *
 * An ad blocker or a failed PostHog init can leave `isReady` false forever. Gating the CTA on it with no
 * escape hatch traded a measurement problem for a REVENUE problem: an eligible unpaid learner would
 * never see the ask at all. An unmeasured conversion is worse than a measured one and better than no
 * conversion, so the wait is bounded.
 */
const MEASURABILITY_GRACE_MS = 4000;
interface LabsTrialBarState {
  /**
   * Whether the CTA can be pressed. False only during the brief window where the arm is still resolving,
   * so a tap cannot produce a conversion with no arm stamped on it.
   */
  ctaEnabled: boolean;
  /** True once the arm resolved and the event will carry a real `arm`. Gates the SHOWN event. */
  measurable: boolean;
  /**
   * Whether the sprint reserves a row for the bar at all.
   *
   * 🔴 DELIBERATELY FLAG-FREE. This decides the LAYOUT: when true, `labs-flow` renders the sprint as a
   * flex column of [scroll pane | bar] and `StepShell` fills that pane with `flex-1` instead of its own
   * viewport-height box. Making it depend on async flag readiness changed the element ANCESTRY of the
   * active screen when the flags landed, which unmounted and REMOUNTED it — wiping the unpersisted
   * interaction state on M3 (picks, reveal) and M4 (free-text answer) out from under the learner, plus a
   * post-hydration layout jump. The structure is now stable from the first paint; only the CTA's enabled
   * state and the event depend on readiness.
   */
  slot: boolean;
}

/**
 * The in-sprint trial bar's state, shared by `labs-flow` (which renders it) and `StepShell` (which has to
 * agree about whether the scroll pane exists, or short screens collapse and lose their centering).
 *
 * `slot` is true when the learner still needs to pay AND is not on the terminal `recommendations` step —
 * that screen carries the pre-paywall hand-off modal, whose own "Start $0 trial" is the measured
 * end-of-sprint ask, and two competing trial CTAs on one screen make a conversion unattributable.
 */
export function useLabsTrialBar() {
  const $ = _c(7);
  const needsPayment = useNeedsPayment();
  const step = useLabsStore(_temp);
  const {
    isReady
  } = useLabsAnalytics();
  const {
    trialBar: surfaceAllowsTrialBar
  } = useLabsSurface();
  const [graceElapsed, setGraceElapsed] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isReady) {
    t0 = () => {
      if (isReady) {
        return;
      }
      const timer = setTimeout(() => setGraceElapsed(true), MEASURABILITY_GRACE_MS);
      return () => clearTimeout(timer);
    };
    t1 = [isReady];
    $[0] = isReady;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const slot = surfaceAllowsTrialBar && needsPayment && step !== "recommendations";
  const t2 = isReady || graceElapsed;
  let t3;
  if ($[3] !== isReady || $[4] !== slot || $[5] !== t2) {
    t3 = {
      ctaEnabled: t2,
      measurable: isReady,
      slot
    };
    $[3] = isReady;
    $[4] = slot;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(s) {
  return s.step;
}